import React, { useState } from 'react';
import { Table, Card, Typography, Select, Space, Image, Tag, Button } from 'antd';
import { useQuery } from 'react-query';
import { contentApi } from '../api';
import { SearchResult, SortField, SortOrder } from '../types';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

const Contents: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<SortField>('publish_time');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const { data, isLoading } = useQuery(
    ['contents', page, pageSize, sortBy, sortOrder],
    () => contentApi.getSearchResults({
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_order: sortOrder,
    }),
    {
      keepPreviousData: true,
    }
  );

  const columns = [
    {
      title: '封面',
      dataIndex: 'cover_url',
      key: 'cover_url',
      width: 80,
      render: (url: string) => (
        <Image
          width={60}
          height={60}
          src={url}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1xnG4W+Q"
        />
      ),
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
      width: 120,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: string) => (
        <Tag color={type === 'video' ? 'blue' : 'green'}>
          {type === 'video' ? '视频' : '图片'}
        </Tag>
      ),
    },
    {
      title: '关键词',
      dataIndex: 'keywords',
      key: 'keywords',
      width: 200,
      render: (keywords: string[]) => (
        <Space wrap>
          {keywords.map(keyword => (
            <Tag key={keyword}>{keyword}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '点赞数',
      dataIndex: 'like_count',
      key: 'like_count',
      width: 80,
    },
    {
      title: '评论数',
      dataIndex: 'comment_count',
      key: 'comment_count',
      width: 80,
    },
    {
      title: '发布时间',
      dataIndex: 'publish_time',
      key: 'publish_time',
      width: 140,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '上次更新',
      dataIndex: 'last_updated',
      key: 'last_updated',
      width: 140,
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_: any, record: SearchResult) => {
        const xsecToken = record.note_data?.xsec_token || '';
        const url = `https://www.xiaohongshu.com/explore/${record.note_id}?xsec_token=${xsecToken}`;
        
        return (
          <Button 
            type="link" 
            onClick={() => window.open(url, '_blank')}
          >
            查看原文
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <Title level={2}>内容查看</Title>
      
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span>排序字段：</span>
          <Select 
            value={sortBy} 
            onChange={setSortBy} 
            style={{ width: 150 }}
          >
            <Option value="publish_time">发布时间</Option>
            <Option value="first_found">首次发现</Option>
            <Option value="like_count">点赞数</Option>
            <Option value="comment_count">评论数</Option>
            <Option value="_id">ID</Option>
          </Select>
          
          <span>排序方向：</span>
          <Select 
            value={sortOrder} 
            onChange={setSortOrder} 
            style={{ width: 100 }}
          >
            <Option value="desc">倒序</Option>
            <Option value="asc">正序</Option>
          </Select>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.data.data}
          loading={isLoading}
          rowKey="id"
          pagination={{
            current: page,
            pageSize: pageSize,
            total: data?.data.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              if (newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setPage(1);
              }
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default Contents; 